import { useMemo } from 'react';

import { createJurisdictionHeaders, isMediaFilled } from '@/data';
import { arcadeApiUrl, findGameMetaData, useArcadeGamesMetaData } from '@/feature/lobby-arcade/hooks/use-arcade-games';
import { useIsNewArcadePlatform } from '@/hooks/use-is-new-arcade-platform';
import { useUserStore } from '@/hooks/use-user-store';
import { useInfiniteQuery } from '@tanstack/react-query';

import { GameResult } from '../components/GameResultItem';

type ResultsAttributes = {
    page?: number;
};

type GetGameHistoryParams = ResultsAttributes & {
    userId: string;
    isNewPlatform: boolean;
};

type GetGameHistoryResponse = {
    data: GameResult[];
    from: number;
    current_page: number;
    last_page: number;
    per_page: number;
    to: number;
    total: number;
};

const getGameHistory = async (params: GetGameHistoryParams): Promise<GetGameHistoryResponse> => {
    const queryParams = new URLSearchParams();
    if (params.page) {
        queryParams.append('page', params.page.toString());
    }
    if (params.userId) {
        queryParams.append('person_id', params.userId);
    }

    const resp = await fetch(`${arcadeApiUrl('/auth/history', params.isNewPlatform)}?${queryParams.toString()}`, {
        method: 'GET',
        headers: {
            ...createJurisdictionHeaders(),
            'Content-Type': 'application/json',
        },
    });
    const data = await resp.json();
    if (!resp.ok) {
        throw data;
    }
    return data;
};

export const useArcadeGameResults = (params: ResultsAttributes, enabled: boolean = true) => {
    const profile = useUserStore(state => state.profile);
    const { data: metaData, isLoading: isMetaDataLoading } = useArcadeGamesMetaData();
    const isNewPlatform = useIsNewArcadePlatform();

    const query = useInfiniteQuery({
        queryKey: ['arcade-game-results', isNewPlatform],
        queryFn: ({ pageParam = 1 }) =>
            getGameHistory({ ...params, page: pageParam, userId: profile.sub, isNewPlatform }),
        initialPageParam: 1,
        getNextPageParam: ({ current_page, last_page }) => {
            return last_page > current_page ? current_page + 1 : undefined;
        },
        enabled,
    });

    const data = useMemo(() => {
        if (!query.data?.pages) {
            return undefined;
        }

        // Map game name and image from metadata
        return {
            ...query.data,
            pages: query.data.pages.map(page => ({
                ...page,
                data: page.data.map(gameResult => {
                    const gameFound = findGameMetaData(metaData, gameResult.description);

                    const name = gameFound?.name;
                    const lobby_image = isMediaFilled(gameFound?.lobby_image) ? gameFound.lobby_image.url : undefined;

                    return {
                        ...gameResult,
                        name,
                        image: lobby_image,
                    };
                }),
            })),
        };
    }, [query.data, metaData]);

    return {
        ...query,
        data,
        isLoading: query.isLoading || isMetaDataLoading,
    };
};
